import React, { useState, useEffect } from "react";
import supabase from "../../../utils/supabase";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface LtiPlatform {
  id: string;
  name: string;
  clientId: string;
  url: string;
  authenticationEndpoint: string;
  accesstokenEndpoint: string;
  authConfig: {
    method: string;
    key: string;
  };
}

interface RegisterPlatformForm {
  name: string;
  clientId: string;
  url: string;
  authenticationEndpoint: string;
  accesstokenEndpoint: string;
  authConfig: {
    method: string;
    key: string;
  };
}

export function LTI() {
  const [platforms, setPlatforms] = useState<LtiPlatform[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [platformToDelete, setPlatformToDelete] = useState<LtiPlatform | null>(
    null
  );
  const [registerForm, setRegisterForm] = useState<RegisterPlatformForm>({
    name: "",
    clientId: "",
    url: "",
    authenticationEndpoint: "",
    accesstokenEndpoint: "",
    authConfig: {
      method: "JWK_SET",
      key: "",
    },
  });

  const fetchPlatforms = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get auth token
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("No authentication token available");
      }

      const response = await fetch(`${API_BASE_URL}/lti/platforms`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let errorMessage = "Failed to fetch platforms";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      let data;
      try {
        const responseText = await response.text();
        if (responseText) {
          data = JSON.parse(responseText);
        } else {
          data = { platforms: [] };
        }
      } catch (parseError) {
        console.error("Failed to parse response:", parseError);
        data = { platforms: [] };
      }

      // Handle case where API returns a single platform object or null
      if (data === null || data === undefined) {
        setPlatforms([]);
      } else if (Array.isArray(data)) {
        // If the API returns an array directly
        setPlatforms(data);
      } else if (data.platforms) {
        // If the API returns an object with platforms array
        setPlatforms(data.platforms);
      } else {
        // Single platform object - wrap it in an array
        setPlatforms([
          {
            id: data.clientId || "platform-1", // Use clientId as ID if no id field
            name: data.name,
            clientId: data.clientId,
            url: data.url,
            authenticationEndpoint: data.authenticationEndpoint,
            accesstokenEndpoint: data.accesstokenEndpoint,
            authConfig: {
              method: data.method || data.authConfig?.method || "JWK_SET",
              key: data.key || data.authConfig?.key || "",
            },
          },
        ]);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch platforms"
      );
    } finally {
      setLoading(false);
    }
  };

  const registerPlatform = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get auth token
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("No authentication token available");
      }

      const response = await fetch(`${API_BASE_URL}/lti/platforms`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(registerForm),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to register platform");
      }

      // Reset form and close form section
      setRegisterForm({
        name: "",
        clientId: "",
        url: "",
        authenticationEndpoint: "",
        accesstokenEndpoint: "",
        authConfig: {
          method: "JWK_SET",
          key: "",
        },
      });
      setShowRegisterForm(false);

      // Refresh platforms list
      await fetchPlatforms();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to register platform"
      );
    } finally {
      setLoading(false);
    }
  };

  const deletePlatform = async () => {
    if (!platformToDelete) return;

    try {
      setLoading(true);
      setError(null);

      // Get auth token
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("No authentication token available");
      }

      const response = await fetch(`${API_BASE_URL}/lti/platforms`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ platformId: platformToDelete.id }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete platform");
      }

      setIsDeleteModalOpen(false);
      setPlatformToDelete(null);

      // Refresh platforms list
      await fetchPlatforms();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete platform"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const handleDeleteClick = (platform: LtiPlatform) => {
    setPlatformToDelete(platform);
    setIsDeleteModalOpen(true);
  };

  const handleEditClick = (platform: LtiPlatform) => {
    setRegisterForm({
      name: platform.name,
      clientId: platform.clientId,
      url: platform.url,
      authenticationEndpoint: platform.authenticationEndpoint,
      accesstokenEndpoint: platform.accesstokenEndpoint,
      authConfig: {
        method: platform.authConfig.method,
        key: platform.authConfig.key,
      },
    });
    setShowRegisterForm(true);
  };

  const resetForm = () => {
    setRegisterForm({
      name: "",
      clientId: "",
      url: "",
      authenticationEndpoint: "",
      accesstokenEndpoint: "",
      authConfig: {
        method: "JWK_SET",
        key: "",
      },
    });
    setShowRegisterForm(false);
  };

  const hasExistingPlatform = platforms.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            LTI Platform Management
          </h2>
          <p className="text-gray-600 mt-1">
            Manage Learning Tools Interoperability (LTI) platform integrations
          </p>
        </div>

        {!hasExistingPlatform && !showRegisterForm && (
          <button
            onClick={() => {
              setShowRegisterForm(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Register New Platform
          </button>
        )}
      </div>

      {/* Register Platform Form Section */}
      {showRegisterForm && (
        <div className="bg-white shadow rounded-lg border border-gray-200 overflow-hidden transition-all duration-300 ease-in-out">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-medium text-gray-900">
              {hasExistingPlatform
                ? "Edit LTI Platform"
                : "Register New LTI Platform"}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {hasExistingPlatform
                ? "Update the details below to modify your LTI platform integration"
                : "Fill in the details below to register a new LTI platform integration"}
            </p>
          </div>

          <div className="p-6">
            <form
              className="space-y-6"
              onSubmit={(e) => {
                e.preventDefault();
                registerPlatform();
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Platform URL *
                  </label>
                  <input
                    type="url"
                    value={registerForm.url}
                    onChange={(e) =>
                      setRegisterForm({ ...registerForm, url: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    placeholder="https://platform.example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Platform Name *
                  </label>
                  <input
                    type="text"
                    value={registerForm.name}
                    onChange={(e) =>
                      setRegisterForm({ ...registerForm, name: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    placeholder="Enter platform name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client ID *
                  </label>
                  <input
                    type="text"
                    value={registerForm.clientId}
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        clientId: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    placeholder="Enter client ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Authentication Endpoint *
                  </label>
                  <input
                    type="url"
                    value={registerForm.authenticationEndpoint}
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        authenticationEndpoint: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    placeholder="https://platform.example.com/auth"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Access Token Endpoint *
                  </label>
                  <input
                    type="url"
                    value={registerForm.accesstokenEndpoint}
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        accesstokenEndpoint: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    placeholder="https://platform.example.com/token"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Auth Method *
                  </label>
                  <select
                    value={registerForm.authConfig.method}
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        authConfig: {
                          ...registerForm.authConfig,
                          method: e.target.value,
                        },
                      })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="JWK_SET">JWK Set</option>
                    <option value="RSA_KEY">RSA Key</option>
                    <option value="RS256">RS256</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Keyset URL *
                  </label>
                  <input
                    type="url"
                    value={registerForm.authConfig.key}
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        authConfig: {
                          ...registerForm.authConfig,
                          key: e.target.value,
                        },
                      })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    placeholder="https://platform.example.com/certs.php"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <svg
                        className="animate-spin h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {hasExistingPlatform ? "Updating..." : "Registering..."}
                    </div>
                  ) : hasExistingPlatform ? (
                    "Update Platform"
                  ) : (
                    "Register Platform"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Platforms List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {hasExistingPlatform ? "Current Platform" : "Registered Platforms"}
          </h3>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <div className="inline-flex items-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Loading platforms...
            </div>
          </div>
        ) : platforms.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No platforms
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by registering a new LTI platform.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Platform
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    URL
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Auth Method
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {platforms.map((platform) => (
                  <tr key={platform.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {platform.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {platform.clientId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {platform.url}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {platform.authConfig.method}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEditClick(platform)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(platform)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && platformToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-lg font-medium text-gray-900">
                  Delete Platform
                </h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete the platform "
                    {platformToDelete.name}"? This action cannot be undone.
                  </p>
                </div>
                <div className="flex justify-center space-x-3">
                  <button
                    onClick={() => setIsDeleteModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={deletePlatform}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {loading ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
